import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import moment from 'moment';

Font.register({
  family: 'Mona-Sans',
  fonts: [
    {src: '/MonaSans-Regular.ttf'},
    {src: '/MonaSans-Medium.ttf', fontWeight: 'medium'},
    {src: '/MonaSans-Bold.ttf', fontWeight: 'bold'},
    {src: '/MonaSans-SemiBold.ttf', fontWeight: 'semibold'},
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingVertical: 25,
    paddingHorizontal: 30,
    fontFamily: 'Mona-Sans',
    fontSize: 10,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 121,
    height: 27,
  },
  headerRef: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  labelIdGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerRefName: {
    color: '#161616',
    fontSize: 10,
    fontWeight: 'medium',
    textTransform: 'uppercase',
  },
  headerRefID: {
    color: '#000000',
    fontSize: 12,
    fontWeight: 'medium',
  },
  statusBadge: {
    fontSize: 8,
    fontWeight: 'medium',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 24,
    textTransform: 'uppercase',
  },
  statusSuccess: {
    color: '#4A878D',
    backgroundColor: '#E2F5F6',
  },
  statusBlock: {
    color: '#C0392B',
    backgroundColor: '#F9EBEB',
  },
  statusPending: {
    color: '#B7791F',
    backgroundColor: '#FEFCBF',
  },
  timestamp: {
    fontSize: 10,
    color: '#161616',
    fontWeight: 'medium',
    textTransform: 'uppercase',
  },
  section: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'medium',
    color: '#0B1C56',
    backgroundColor: '#EEF3FC',
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 4,
  },
  sectionContent: {
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 12,
    paddingBottom: 12,
    border: '1px solid #EEF3FC',
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
  },
  gridContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 12,
  },
  dataPair: {
    flexGrow: 1,
    flexShrink: 0,
    paddingRight: 15,
    marginBottom: 10,
  },
  dataKey: {
    fontSize: 10,
    color: '#161616',
    textTransform: 'uppercase',
    fontWeight: 'semibold',
    marginBottom: 5,
  },
  dataValue: {
    fontSize: 12,
    fontWeight: 'medium',
    color: '#4E4E4E',
    lineHeight: 1.4,
    maxWidth: '100%',
  },
  footer: {
    flexDirection: 'column',
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#E4E4E6',
    alignItems: 'center',
  },
  footerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 8,
  },
  footerText: {
    fontSize: 9,
    color: '#858DAA',
    fontWeight: 'bold',
    textAlign: 'center',
    width: '33%',
  },
});

const DataPair = ({label, value, style = {}}) => (
  <View style={[styles.dataPair, style]}>
    <Text style={styles.dataKey}>{label}</Text>
    <Text style={styles.dataValue}>{value || '-'}</Text>
  </View>
);

export const StrDocument = ({
  caseDetails,
  logoImage,
  reportingUser,
  userDetails,
}) => {
  const rootTx = caseDetails?.transaction || {};
  const ev = rootTx?.event || {};
  const esdUser = rootTx?.esd_user || {};
  const evTx = ev?.transaction || {};
  const evUser = ev?.user || {};
  const evSender = ev?.sender || {};
  const evReceiver = ev?.receiver || {};
  const evAddress = ev?.address || {};
  const evaluationResult = rootTx?.evaluation_result || {};
  const triggeredRule = evaluationResult?.triggered_rules?.[0];
  const currency = evTx?.currency || 'NGN';
  const amount = evTx?.amount || '0.00';

  const entityDetails =
    userDetails?.company?.setup_config?.entity_details || {};

  const fullAddress = evAddress
    ? [evAddress.street, evAddress.city, evAddress.country]
        .filter(Boolean)
        .join(', ')
    : null;

  let narrative = null;
  if (triggeredRule?.message) {
    narrative = `Flagged due to: ${triggeredRule.message}. Risk Score: ${
      evaluationResult?.risk_score || '-'
    }.`;
  }

  const currentDate = moment().format('DD/MM/YYYY, hh:mmA').toUpperCase();
  const suspiciousDate = evTx?.time
    ? moment(evTx.time).format('Do MMM, YYYY')
    : null;
  const submissionDate = moment().format('Do MMM, YYYY');

  const reporterName = reportingUser
    ? `${reportingUser.first_name} ${reportingUser.last_name}`
    : null;

  const getStatusStyle = status => {
    const normalizedStatus = status ? status.toUpperCase() : '';
    let colorStyle = styles.statusPending;
    if (normalizedStatus === 'ALLOW') colorStyle = styles.statusSuccess;
    else if (normalizedStatus === 'BLOCK') colorStyle = styles.statusBlock;
    return [styles.statusBadge, colorStyle];
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} wrap={false}>
          {logoImage && <Image style={styles.logo} src={logoImage} />}
        </View>

        <View style={styles.headerRef} wrap={false}>
          <View style={styles.labelIdGroup}>
            <Text style={styles.headerRefName}>PROFILEDRISK CASE ID</Text>
            <Text style={styles.headerRefID}>{evTx?.id || '-'}</Text>
          </View>
          <Text style={getStatusStyle(rootTx?.final_outcome)}>
            {rootTx?.final_outcome || 'PENDING'}
          </Text>
        </View>

        <View style={styles.headerRef} wrap={false}>
          <View style={styles.labelIdGroup}>
            <Text style={styles.headerRefName}>PROFILEDRISK PROFILE ID</Text>
            <Text style={styles.headerRefID}>{evUser?.user_id || '-'}</Text>
          </View>
          <Text style={styles.timestamp}>{currentDate}</Text>
        </View>

        {/* ... Risk Intelligence Section ... */}
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Risk Intelligence</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="CUSTOMER RISK SCORE"
                value={
                  evaluationResult?.risk_score
                    ? `${evaluationResult.risk_score}/100`
                    : null
                }
                style={{width: '50%', flexGrow: 0}}
              />
              <DataPair
                label="CUSTOMER RISK TIER"
                value={esdUser?.tier}
                style={{width: '50%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Suspicious Details</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="DATE(S) OF SUSPICIOUS ACTIVITY"
                value={suspiciousDate}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="TOTAL VALUE INVOLVED"
                value={
                  evTx?.amount ? `${currency.toUpperCase()} ${amount}` : null
                }
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="REASONS FOR SUSPICION"
                value={triggeredRule?.message}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="TRANSACTION TYPE"
                value={evTx?.type}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="DETAILED NARRATIVE"
                value={narrative}
                style={{width: '66%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Suspect/person involved</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="FULL NAME"
                value={evUser?.name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="DATE OF BIRTH"
                value={null}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ADDRESS"
                value={fullAddress}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ID TYPE"
                value={'User ID'}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ID NUMBER"
                value={evUser?.user_id}
                style={{width: '66%', flexGrow: 0}}
              />
              <DataPair
                label="SOURCE ACCOUNT NUMBER"
                value={evSender?.account_number}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="SOURCE ACCOUNT NAME"
                value={evSender?.account_name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="DESTINATION ACCOUNT NUMBER"
                value={evReceiver?.account_number}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="DESTINATION ACCOUNT NAME"
                value={evReceiver?.account_name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="RELATIONSHIP TO REPORTING ENTITY"
                value={'Customer'}
                style={{width: '33%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Counterparty</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="FULL NAME"
                value={evReceiver?.account_name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ACCOUNT NUMBER(S) INVOLVED"
                value={evReceiver?.account_number}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="ADDRESS"
                value={evReceiver?.country}
                style={{width: '33%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>Reporting Entity</Text>
          <View style={styles.sectionContent}>
            <View style={styles.gridContainer}>
              <DataPair
                label="REPORTING ENTITY NAME"
                value={entityDetails?.reporting_entity_name}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="RENDERING ENTITY CODE"
                value={entityDetails?.rendering_entity_code}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="SUBMISSION DATE"
                value={submissionDate}
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="COMPLIANCE OFFICER"
                value={
                  reporterName &&
                  `Name : ${reporterName || '-'}\nPhone : ${
                    reportingUser?.phone || '-'
                  }\nEmail : ${reportingUser?.email || '-'}`
                }
                style={{width: '33%', flexGrow: 0}}
              />
              <DataPair
                label="BRANCH/LOCATION OF SUSPICION"
                value={entityDetails?.branch_name_or_id}
                style={{width: '66%', flexGrow: 0}}
              />
            </View>
          </View>
        </View>

        <View style={styles.footer} wrap={false}>
          <View style={styles.footerTopRow}>
            <Text style={styles.footerText}>
              GENERATED BY: {(reporterName || 'N/A').toUpperCase()}
            </Text>
            <Text style={styles.footerText}>GENERATED: {currentDate}</Text>
            <Text style={styles.footerText}>DOWNLOADED: {currentDate}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
